import { Link } from 'react-router-dom';
import type { Comic } from '@/mocks/comics';

interface ComicCardProps {
  comic: Comic;
  showCategory?: boolean;
}

export default function ComicCard({ comic, showCategory = true }: ComicCardProps) {
  const statusLabel = comic.status === 'ongoing' ? '连载中' : '已完结';
  const statusClass = comic.status === 'ongoing'
    ? 'bg-accent-500 text-background-50'
    : 'bg-secondary-500 text-background-50';

  return (
    <Link
      to={`/comic/${comic.id}`}
      className="group block rounded-xl overflow-hidden bg-white border border-background-200/70 hover:border-primary-200 transition-all duration-300"
      data-product-shop
    >
      {/* Cover Image */}
      <div className="relative aspect-[3/4] overflow-hidden">
        <img
          src={comic.cover}
          alt={`${comic.title} - 韩国漫画`}
          title={`${comic.title} - 韩国漫画在线免费阅读`}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        {/* Status Badge */}
        <span className={`absolute top-2 left-2 px-2 py-0.5 rounded text-xs font-medium ${statusClass}`}>
          {statusLabel}
        </span>
        {/* Rating */}
        <div className="absolute top-2 right-2 flex items-center gap-1 bg-black/60 backdrop-blur-sm rounded-full px-2 py-0.5">
          <i className="ri-star-fill text-yellow-400 text-xs"></i>
          <span className="text-xs text-white font-medium">{comic.rating}</span>
        </div>
        {/* Hover Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
          <div className="w-full">
            <p className="text-white text-sm line-clamp-3 leading-relaxed">{comic.description}</p>
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="p-3 md:p-4">
        <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-800 truncate group-hover:text-primary-500 transition-colors duration-200">
          {comic.title}
        </h3>
        <p className="text-xs text-foreground-500 mt-1">{comic.author}</p>

        {showCategory && comic.categories.length > 0 && (
          <div className="flex items-center gap-1.5 mt-2 flex-wrap">
            {comic.categories.slice(0, 2).map((catSlug) => (
              <span
                key={catSlug}
                className="px-2 py-0.5 rounded text-xs bg-secondary-100 text-secondary-700"
              >
                {catSlug === 'love' ? '恋爱' : catSlug === 'action' ? '动作' : catSlug === 'fantasy' ? '奇幻' : catSlug === 'thriller' ? '悬疑' : catSlug === 'comedy' ? '喜剧' : catSlug === 'drama' ? '剧情' : catSlug === 'school' ? '校园' : catSlug === 'bl' ? '耽美' : catSlug === 'horror' ? '恐怖' : catSlug}
              </span>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between mt-2.5 text-xs text-foreground-400">
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {comic.views > 10000 ? `${(comic.views / 10000).toFixed(0)}万` : comic.views}
          </span>
          <span>{comic.chapters.length}话</span>
        </div>
      </div>
    </Link>
  );
}